import type { Session } from "../api/types.ts";

const uuid =
  /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/;
const roles = new Set(["agent", "supervisor", "engineer", "admin"]);
export function validateSupportAuthority(
  value: unknown,
  expectedAccount: string,
): Session {
  const session = value as Session | null;
  const a = session?.actor;
  if (
    !uuid.test(expectedAccount) ||
    !a ||
    a.account_id !== expectedAccount ||
    a.account_type !== "wenlix_internal_manager" ||
    (a.tenant_id !== undefined && a.tenant_id !== "") ||
    a.active !== true ||
    typeof a.assignment_eligible !== "boolean" ||
    typeof a.escalation_eligible !== "boolean" ||
    session?.auth_mode !== "provider" ||
    !roles.has(a.support_role ?? "") ||
    !Number.isSafeInteger(a.version) ||
    a.version < 1 ||
    typeof a.display_name !== "string" ||
    [...a.display_name].length > 256 ||
    !Array.isArray(a.capabilities) ||
    !a.capabilities.every(
      (c) => typeof c === "string" && /^[a-z][a-z0-9_.]{1,99}$/.test(c),
    ) ||
    new Set(a.capabilities).size !== a.capabilities.length ||
    !Array.isArray(session.capabilities) ||
    session.capabilities.length !== a.capabilities.length ||
    new Set(session.capabilities).size !== session.capabilities.length ||
    !session.capabilities.every((key) => a.capabilities.includes(key)) ||
    !Array.isArray(session.memberships)
  ) {
    throw new Error("Не удалось подтвердить доступ к поддержке.");
  }
  return session;
}
export const supportPath = (pathname: string) =>
  /^\/support(?:\/|$)/.test(pathname);
export function capability(session: Session, key: string): boolean {
  return (
    session.actor.active &&
    session.actor.account_type === "wenlix_internal_manager" &&
    roles.has(session.actor.support_role ?? "") &&
    session.actor.capabilities.includes(key)
  );
}
export const supervisory = (session: Session) =>
  capability(session, "support.tickets.all");

export const canReceiveAssignment = (session: Session) =>
  session.actor.assignment_eligible === true &&
  capability(session, "support.tickets.read") && capability(session, "support.tickets.write");

// Backend human messages are not a trusted UI vocabulary. No URLs, stack traces
// or transport identifiers are printed in the normal customer/manager surface.
export function supportError(error: unknown): string {
  const status = (error as { status?: number })?.status;
  const code = (error as { code?: string })?.code;
  if (status === 400 && code === "reason_required")
    return "Кратко опишите, что нужно проверить. До 1000 символов.";
  if (status === 400 && code === "invalid_scope")
    return "Выберите разделы и действия для запроса доступа.";
  if (status === 400 && code === "invalid_duration")
    return "Укажите срок доступа в будущем, не более 30 дней.";
  if (status === 403 && code === "resource_capability_not_allowed")
    return "Эти действия недоступны для выбранных объектов. Обновите условия доступа.";
  if (status === 401) return "Вход завершён. Войдите снова.";
  if (status === 403 || status === 404)
    return "Доступ недоступен. Проверьте назначение и разрешение клиента.";
  if (status === 409)
    return "Данные изменились. Обновите обращение и проверьте действие.";
  if (status === 413) return "Файл слишком большой.";
  if (status === 429) return "Слишком много запросов. Попробуйте чуть позже.";
  if (status === 0)
    return "Нет соединения. Проверьте интернет и повторите попытку.";
  return "Не удалось выполнить действие. Попробуйте ещё раз.";
}
